import {
  ChangeDetectionStrategy, Component, ElementRef,
  computed, effect, inject, signal, viewChild, untracked,
} from '@angular/core';
import {
  PaperButtonComponent,
  PaperIconComponent,
  PaperSegmentedControlComponent,
  type PaperSegmentedControlOption,
} from '@ricoh-paper/angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { DelegatePickerService } from './delegate-picker.service';
import { DelegateSearchService } from '../../../core/services/delegate-search.service';
import { PersonSearchService } from '../../../core/services/person-search.service';
import { LocationStateService } from '../../../core/services/location-state.service';
import { Person, WORK_STATUS_TO_EN, STATUS_ICONS, WorkStatus } from '../../../core/models/person.model';
import { localizeStatus } from '../../../core/constants/status-labels';
import { getInitials } from '../../../core/utils/text';
import { TranslocoService } from '@jsverse/transloco';
import { FocusTrapDirective } from '../../directives/focus-trap.directive';
import { PersonGroupsService } from '../../../core/services/person-groups.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../core/analytics/events';
import { TranslocoPipe } from '@jsverse/transloco';

/**
 * Person Picker Dialog — modal overlay for selecting a person.
 * Supports two modes:
 *   delegate — uses DelegateSearchService (permission-gated) for booking on behalf
 *   find — uses PersonSearchService (general search) for finding colleagues
 *
 * Triggered programmatically via DelegatePickerService.
 */
@Component({
  selector: 'im-delegate-picker-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, PaperIconComponent, PaperSegmentedControlComponent, FocusTrapDirective,
    TranslocoPipe,
  ],
  templateUrl: './delegate-picker-dialog.component.html',
  styleUrl: './delegate-picker-dialog.component.scss',
})
export class DelegatePickerDialogComponent {
  protected readonly pickerService = inject(DelegatePickerService);
  private readonly delegateSearch = inject(DelegateSearchService);
  private readonly personSearch = inject(PersonSearchService);
  private readonly locationState = inject(LocationStateService);
  private readonly transloco = inject(TranslocoService);

  protected readonly groups = inject(PersonGroupsService);
  private readonly analytics = inject(AnalyticsService);
  protected readonly filter = signal<'people' | 'favourites'>('people');
  /** The loaded dictionary, so the filter labels follow a language change. */
  private readonly dictionary = toSignal(this.transloco.selectTranslation(), { initialValue: {} });
  protected readonly filterOptions = computed<PaperSegmentedControlOption[]>(() => {
    void this.dictionary();
    return [
      { value: 'people', label: this.transloco.translate('shared.delegate_picker_dialog.people'), icon: 'people' },
      { value: 'favourites', label: this.transloco.translate('shared.delegate_picker_dialog.favourites'), icon: 'heart' },
    ];
  });
  protected readonly failed = this.delegateSearch.error;
  protected readonly query = signal('');
  protected readonly selectedIndex = signal(0);

  readonly resultList = viewChild<ElementRef<HTMLElement>>('resultList');
  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('pickerInput');

  /** Results from the active search service based on current mode */
  protected readonly results = computed<Person[]>(() => {
    if (this.pickerService.mode() === 'delegate') {
      // Map DelegateEmployee[] to Person[]
      return this.delegateSearch.results().map(e => ({
        ...e,
        status: undefined,
        currentSpaceId: undefined,
        currentSpaceName: undefined,
        currentFloorName: undefined,
      }));
    }
    return this.personSearch.results();
  });

  protected readonly searching = computed(() => {
    return this.pickerService.mode() === 'delegate'
      ? this.delegateSearch.searching()
      : this.personSearch.searching();
  });

  protected readonly hasResults = computed(() => this.results().length > 0);

  constructor() {
    // Focus input when dialog opens
    effect(() => {
      if (this.pickerService.open()) {
        untracked(() => {
        this.filter.set('people');
        this.query.set('');
        this.selectedIndex.set(0);
        this.delegateSearch.clear();
        this.personSearch.clear();
        void this.groups.load();
        this.search();
        });
      }
    });
  }

  onInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.query.set(term);
    this.selectedIndex.set(0);
    this.search();
    this.track('search');
  }

  protected async setFilter(filter: 'people' | 'favourites'): Promise<void> {
    this.filter.set(filter);
    this.selectedIndex.set(0);
    this.delegateSearch.clear();
    if (filter === 'favourites') await this.groups.load();
    if (!this.pickerService.open() || this.filter() !== filter) return;
    this.search();
    this.track('filter');
  }

  protected search(): void {
    const locationId = this.locationState.selectedLocationId();
    if (!locationId) return;
    if (this.pickerService.mode() === 'delegate') {
      const ids = this.groups.defaultGroup()?.memberIds ?? [];
      const people = ids.flatMap(id => {
        const person = this.groups.personCache().get(id);
        return person?.email ? [{ id, email: person.email }] : [];
      });
      this.delegateSearch.search(this.query(), locationId,
        this.filter() === 'favourites' ? people : undefined);
    } else this.personSearch.search(this.query(), locationId);
  }

  private track(action: 'search' | 'filter' | 'select' | 'cancel', employeeId?: string): void {
    this.analytics.capture(ANALYTICS_EVENTS.PERSON_PICKER_ACTION, {
      mode: this.pickerService.mode(), action, filter: this.filter(),
      result_count: this.results().length, ...(employeeId ? { employee_id: employeeId } : {}),
    });
  }

  onKeydown(event: KeyboardEvent): void {
    const results = this.results();

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancel();
      return;
    }
    // Tab uses the real modal focus trap; Enter on buttons keeps native behaviour.
    if (event.target !== this.searchInput()?.nativeElement) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex.update(i => Math.max(0, Math.min(i + (event.key === 'ArrowDown' ? 1 : -1), results.length - 1)));
      this.resultList()?.nativeElement.querySelector(`#delegate-person-${this.selectedIndex()}`)?.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && results[this.selectedIndex()]) {
      event.preventDefault();
      this.selectPerson(results[this.selectedIndex()]);
    }
  }

  selectPerson(person: Person): void {
    this.track('select', person.id);
    this.delegateSearch.clear();
    this.personSearch.clear();
    this.pickerService.resolve(person);
  }

  cancel(): void {
    this.track('cancel');
    this.delegateSearch.clear();
    this.personSearch.clear();
    this.pickerService.resolve(null);
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('picker-backdrop')) {
      this.cancel();
    }
  }

  getInitials(name: string): string {
    return getInitials(name);
  }

  statusLabel(status: string): string {
    const canonicalEn = WORK_STATUS_TO_EN[status as WorkStatus] ?? '';
    if (!canonicalEn) return '';
    return localizeStatus(canonicalEn, this.transloco);
  }

  statusIcon(status: string): string {
    return STATUS_ICONS[status as keyof typeof STATUS_ICONS] ?? '';
  }
}
